import { format } from 'date-fns';

const COLORS = {
    primary: '#10b981',
    secondary: '#3b82f6',
    accent: '#8b5cf6',
    success: '#22c55e',
};

// Format date with timezone awareness
const formatTime = (dateStr: string, fmt: string, useLocalTime: boolean): string => {
    const date = new Date(dateStr);
    if (useLocalTime) return format(date, fmt);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return format(utcDate, fmt) + ' UTC';
};

interface PageView {
    id: number;
    path: string;
    title: string | null;
    timestamp: string;
    duration: number | null;
}

interface Event {
    id: number;
    eventName: string;
    eventCategory: string;
    page: string | null;
    timestamp: string;
    eventData: any;
}

interface Session {
    sessionId: string;
    startTime: string;
    endTime: string | null;
    duration: number | null;
    entryPage: string | null;
    exitPage: string | null;
    referrer: string | null;
    referrerDomain: string | null;
    utmSource: string | null;
    utmMedium: string | null;
    utmCampaign: string | null;
    pageViews: PageView[];
    events: Event[];
    pageCount: number;
}

interface VisitorDetails {
    visitorId: string;
    firstVisit: string;
    lastVisit: string;
    totalVisits: number;
    isRegistered: boolean;
    userId: number | null;
    deviceType: string | null;
    browser: string | null;
    os: string | null;
    screenWidth: number | null;
    screenHeight: number | null;
    viewportWidth: number | null;
    viewportHeight: number | null;
    colorDepth: number | null;
    touchSupport: boolean | null;
    connectionType: string | null;
    cpuCores: number | null;
    deviceMemory: number | null;
    timezone: string | null;
    language: string | null;
    country: string | null;
    city: string | null;
    sessions: Session[];
}

interface VisitorDetailsModalProps {
    visitor: VisitorDetails | null;
    onClose: () => void;
    loading: boolean;
    useLocalTime?: boolean;
}

export const VisitorDetailsModal = ({ visitor, onClose, loading, useLocalTime = true }: VisitorDetailsModalProps) => {
    if (!visitor && !loading) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            onClick={handleBackdropClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000,
                padding: '24px',
                overflowY: 'auto',
            }}
        >
            <div
                style={{
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    borderRadius: '20px',
                    border: '1px solid #334155',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
                    maxWidth: '900px',
                    width: '100%',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <div
                    style={{
                        padding: '24px',
                        borderBottom: '1px solid #334155',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div>
                        <h2 style={{ color: '#f1f5f9', fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
                            Visitor Details
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '13px' }}>
                            {loading ? 'Loading...' : `ID: ${visitor?.visitorId.substring(0, 12)}...`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#94a3b8',
                            fontSize: '28px',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            lineHeight: '1',
                            transition: 'color 0.2s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = '#f1f5f9')}
                        onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                            <div style={{ fontSize: '16px' }}>Loading visitor details...</div>
                        </div>
                    ) : visitor ? (
                        <>
                            {/* Overview Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Device</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>{visitor.deviceType || 'Unknown'}</div>
                                </div>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Browser</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>{visitor.browser || 'Unknown'}</div>
                                </div>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>OS</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>{visitor.os || 'Unknown'}</div>
                                </div>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Timezone</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>{visitor.timezone || 'Unknown'}</div>
                                </div>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Dimensions</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>
                                        {visitor.screenWidth && visitor.screenHeight ? `${visitor.screenWidth} × ${visitor.screenHeight}` : 'Unknown'}
                                    </div>
                                </div>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155' }}>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Location</div>
                                    <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600' }}>
                                        {visitor.city && visitor.country ? `${visitor.city}, ${visitor.country}` : visitor.country || 'Unknown'}
                                    </div>
                                </div>
                            </div>

                            {/* Device Capabilities - Expandable */}
                            <details style={{ marginBottom: '24px' }}>
                                <summary style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '13px', fontWeight: '600', padding: '12px 16px', background: '#0f172a', borderRadius: '12px', border: '1px solid #334155', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>🔧 Device Capabilities</span>
                                    <span style={{ color: COLORS.primary }}>▼</span>
                                </summary>
                                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '0 0 12px 12px', border: '1px solid #334155', borderTop: 'none', marginTop: '-12px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Viewport Size</div>
                                            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>
                                                {visitor.viewportWidth && visitor.viewportHeight ? `${visitor.viewportWidth} × ${visitor.viewportHeight}` : 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Color Depth</div>
                                            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{visitor.colorDepth ? `${visitor.colorDepth}-bit` : 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Touch Support</div>
                                            <div style={{ color: visitor.touchSupport ? COLORS.success : '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>
                                                {visitor.touchSupport === null ? 'N/A' : visitor.touchSupport ? '✓ Yes' : '✗ No'}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Connection</div>
                                            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{visitor.connectionType || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>CPU Cores</div>
                                            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{visitor.cpuCores || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Device Memory</div>
                                            <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{visitor.deviceMemory ? `${visitor.deviceMemory} GB` : 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </details>

                            {/* Visit Info */}
                            <div style={{ background: '#0f172a', padding: '16px', borderRadius: '12px', border: '1px solid #334155', marginBottom: '24px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                                    <div>
                                        <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>First Visit</div>
                                        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>
                                            {formatTime(visitor.firstVisit, 'MMM dd, yyyy HH:mm', useLocalTime)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Last Visit</div>
                                        <div style={{ color: COLORS.success, fontSize: '14px', fontWeight: '600' }}>
                                            {formatTime(visitor.lastVisit, 'MMM dd, yyyy HH:mm', useLocalTime)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Total Visits</div>
                                        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600' }}>{visitor.totalVisits}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Status</div>
                                        <span
                                            style={{
                                                background: visitor.isRegistered ? COLORS.success : '#334155',
                                                color: '#fff',
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                display: 'inline-block',
                                            }}
                                        >
                                            {visitor.isRegistered ? 'Registered' : 'Guest'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Sessions */}
                            <div>
                                <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>
                                    Sessions ({visitor.sessions.length})
                                </h3>
                                {visitor.sessions.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#0f172a', borderRadius: '12px', border: '1px solid #334155' }}>
                                        No sessions found
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {visitor.sessions.map((session) => (
                                            <details
                                                key={session.sessionId}
                                                style={{
                                                    background: '#0f172a',
                                                    borderRadius: '12px',
                                                    border: '1px solid #334155',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <summary
                                                    style={{
                                                        padding: '16px',
                                                        cursor: 'pointer',
                                                        listStyle: 'none',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        transition: 'background 0.2s',
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.background = '#1e293b')}
                                                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                                >
                                                    <div>
                                                        <div style={{ color: '#f1f5f9', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                                                            {formatTime(session.startTime, 'MMM dd, yyyy HH:mm:ss', useLocalTime)}
                                                        </div>
                                                        <div style={{ color: '#64748b', fontSize: '12px' }}>
                                                            {session.duration ? `${Math.floor(session.duration / 60)}m ${session.duration % 60}s` : 'Active'} • {session.pageCount} pages
                                                            {session.utmCampaign && ` • Campaign: ${session.utmCampaign}`}
                                                        </div>
                                                    </div>
                                                    <div style={{ color: COLORS.primary, fontSize: '12px', fontWeight: '600' }}>▼</div>
                                                </summary>
                                                <div style={{ padding: '0 16px 16px', borderTop: '1px solid #334155' }}>
                                                    {/* Session Details */}
                                                    <div style={{ padding: '12px 0', borderBottom: '1px solid #334155', marginBottom: '12px' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: '13px' }}>
                                                            {session.entryPage && (
                                                                <>
                                                                    <div style={{ color: '#94a3b8' }}>Entry:</div>
                                                                    <div style={{ color: '#f1f5f9' }}>{session.entryPage}</div>
                                                                </>
                                                            )}
                                                            {session.exitPage && (
                                                                <>
                                                                    <div style={{ color: '#94a3b8' }}>Exit:</div>
                                                                    <div style={{ color: '#f1f5f9' }}>{session.exitPage}</div>
                                                                </>
                                                            )}
                                                            {session.referrerDomain && (
                                                                <>
                                                                    <div style={{ color: '#94a3b8' }}>Referrer:</div>
                                                                    <div style={{ color: '#f1f5f9' }}>{session.referrerDomain}</div>
                                                                </>
                                                            )}
                                                            {session.utmSource && (
                                                                <>
                                                                    <div style={{ color: '#94a3b8' }}>UTM Source:</div>
                                                                    <div style={{ color: '#f1f5f9' }}>{session.utmSource}</div>
                                                                </>
                                                            )}
                                                            {session.utmMedium && (
                                                                <>
                                                                    <div style={{ color: '#94a3b8' }}>UTM Medium:</div>
                                                                    <div style={{ color: '#f1f5f9' }}>{session.utmMedium}</div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Page Views */}
                                                    {session.pageViews.length > 0 && (
                                                        <div style={{ marginBottom: '12px' }}>
                                                            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
                                                                Page Views ({session.pageViews.length})
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                {session.pageViews.map((pv) => (
                                                                    <div
                                                                        key={pv.id}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            background: '#1e293b',
                                                                            borderRadius: '6px',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <div style={{ color: '#f1f5f9', fontSize: '13px' }}>{pv.path}</div>
                                                                            {pv.title && <div style={{ color: '#64748b', fontSize: '11px' }}>{pv.title}</div>}
                                                                        </div>
                                                                        <div style={{ color: '#94a3b8', fontSize: '11px', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                                                            {formatTime(pv.timestamp, 'HH:mm:ss', useLocalTime)}
                                                                            {pv.duration && ` • ${pv.duration}s`}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Events */}
                                                    {session.events.length > 0 && (
                                                        <div>
                                                            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
                                                                Events ({session.events.length})
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                {session.events.map((event) => (
                                                                    <div
                                                                        key={event.id}
                                                                        style={{
                                                                            padding: '8px 12px',
                                                                            background: '#1e293b',
                                                                            borderRadius: '6px',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                        }}
                                                                    >
                                                                        <div>
                                                                            <div style={{ color: COLORS.accent, fontSize: '13px', fontWeight: '600' }}>{event.eventName}</div>
                                                                            <div style={{ color: '#64748b', fontSize: '11px' }}>
                                                                                {event.eventCategory}
                                                                                {event.page && ` • ${event.page}`}
                                                                            </div>
                                                                        </div>
                                                                        <div style={{ color: '#94a3b8', fontSize: '11px', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                                                                            {formatTime(event.timestamp, 'HH:mm:ss', useLocalTime)}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </details>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};
